import React, { useState } from 'react';
import { 
  Database, 
  Layers, 
  Cpu, 
  Globe, 
  Code, 
  Zap, 
  FileJson, 
  CheckCircle, 
  ArrowRight, 
  Terminal,
  Map as MapIcon,
  Box,
  Loader,
  Shield,
  Activity,
  Server,
  Briefcase,
  Leaf,
  ShoppingCart,
  Anchor,
  Building,
  MapPin,
  Grid3x3,
  Home,
  Mail,
  MessageSquare
} from 'lucide-react';

export default function GeoRTELanding() {
  const [formStatus, setFormStatus] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Form State with new fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: 'Real Estate',
    role: 'Data Analyst',
    region: 'North America'
  });

  // --- Form Handling ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    
    try {
      const response = await fetch(import.meta.env.VITE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: `Industry: ${formData.industry}, Role: ${formData.role}, Region: ${formData.region}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit form');
      }

      const data = await response.json();
      console.log('Success:', data);
      
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        industry: '',
        role: '',
        region: ''
      });
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error:', error);
      setSubmitError(error.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Visual Background ---
  const BackgroundContours = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-15">
      <svg width="100%" height="100%" preserveAspectRatio="none" className="stroke-emerald-500/30 fill-none stroke-[0.5]">
         <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
            </pattern>
         </defs>
         <rect width="100%" height="100%" fill="url(#grid)" />
         <path d="M0,300 Q400,100 800,300 T1600,300" />
         <path d="M0,400 Q400,200 800,400 T1600,400" />
         <path d="M0,500 Q400,300 800,500 T1600,500" />
      </svg>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans selection:bg-emerald-500 selection:text-white">
      {/* --- Navigation --- */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/90 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="GeoRTE" className="h-10" />
            <span className="font-bold text-xl tracking-tighter">Geo<span className="text-emerald-400">RTE</span></span>
          </div>
          <a href="#waitlist" className="hidden sm:block px-4 py-2 rounded-full border border-emerald-500/30 text-emerald-400 text-xs font-mono hover:bg-emerald-500/10 transition-colors">
            Beta Access
          </a>
        </div>
      </nav>

      {/* --- 1. Hero Section --- */}
      <header className="relative pt-32 pb-24 sm:pt-48 sm:pb-32 overflow-hidden">
        <BackgroundContours />
        <div className="relative max-w-7xl mx-auto px-6 text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 border border-slate-700 text-xs font-mono text-emerald-400 mb-8 shadow-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Coming Soon
          </div>
          
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-8 leading-[1.1] text-white">
            Skip the Data Prep. <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500">
              Jump Straight to Spatial Insights.
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
            GeoRTE delivers <strong className="text-slate-100">&quot;ready-to-eat&quot; spatial data</strong>—precisely what you need, exactly when you need it. 
            Fuel your ideas and supercharge your dashboards with <strong className="text-slate-100">No-ETL</strong> datasets, removing the friction of complex schemas.
          </p>

          <a 
            href="#waitlist" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-lg transition-all shadow-[0_0_30px_rgba(16,185,129,0.25)] text-lg group"
          >
            Get Early Access <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform"/>
          </a>
        </div>
      </header>

      {/* --- 2. The Dual Offering (Strict Requirement) --- */}
      <section className="py-20 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
             <h2 className="text-3xl font-bold mb-4">One Platform. Triple Power.</h2>
             <p className="text-slate-400">From ready-made datasets to fully managed spatial infrastructure.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Offering A: Analysts/Devs */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-emerald-500/50 transition-all group relative overflow-hidden">
               <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Code size={80} />
               </div>
               <div className="relative z-10">
                 <div className="w-12 h-12 bg-emerald-900/30 rounded-lg flex items-center justify-center mb-6 border border-emerald-500/20 text-emerald-400">
                    <Terminal size={24} />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">Easy-to-Use Datasets</h3>
                 <p className="text-emerald-400 font-mono text-xs uppercase tracking-wider mb-4">For Analysts & Developers</p>
                 <p className="text-slate-400 mb-6 leading-relaxed text-sm">
                    Comfortable interfaces (REST APIs, Python SDK) with simplified schemas. 
                    Comprehensive documentation ensures you spend less time guessing column names and more time building.
                 </p>
                 <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-emerald-500"/> GIS Data Integration made simple</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-emerald-500"/> Standardized GeoJSON/Parquet</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-emerald-500"/> Instant Preview & Query</li>
                 </ul>
               </div>
            </div>

            {/* Offering B: Data Scientists (AI-Ready) */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-indigo-500/50 transition-all group relative overflow-hidden">
               <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Cpu size={80} />
               </div>
               <div className="relative z-10">
                 <div className="w-12 h-12 bg-indigo-900/30 rounded-lg flex items-center justify-center mb-6 border border-indigo-500/20 text-indigo-400">
                    <Zap size={24} />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">AI-Ready Datasets</h3>
                 <p className="text-indigo-400 font-mono text-xs uppercase tracking-wider mb-4">For Data Scientists</p>
                 <p className="text-slate-400 mb-6 leading-relaxed text-sm">
                    Download, integrate, and train models instantly. Pre-normalized, cleaned, and formatted for ML pipelines. 
                    <span className="text-indigo-300"> No pre-processing required.</span>
                 </p>
                 <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-indigo-500"/> Model-ready formats</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-indigo-500"/> Feature engineering included</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-indigo-500"/> Optimized for training</li>
                 </ul>
               </div>
            </div>

            {/* Offering C: Professional Service */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-orange-500/50 transition-all group relative overflow-hidden">
               <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Server size={80} />
               </div>
               <div className="relative z-10">
                 <div className="w-12 h-12 bg-orange-900/30 rounded-lg flex items-center justify-center mb-6 border border-orange-500/20 text-orange-400">
                    <Shield size={24} />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">Professional Service</h3>
                 <p className="text-orange-400 font-mono text-xs uppercase tracking-wider mb-4">Spatial Backend-as-a-Service</p>
                 <p className="text-slate-400 mb-6 leading-relaxed text-sm">
                    Complete spatial infrastructure from complex ETL pipelines to managed API hosting. 
                    <span className="text-orange-300"> We bridge the gap between raw data and your application.</span>
                 </p>
                 <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-orange-500"/> Custom ETL pipelines</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-orange-500"/> Managed API hosting</li>
                    <li className="flex gap-2 items-center"><CheckCircle size={16} className="text-orange-500"/> Production-ready solutions</li>
                 </ul>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 3. Value Proposition Grid (The "Why") --- */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
           <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                 {
                    icon: <Database className="text-blue-400" />,
                    title: "One Unified Source",
                    desc: "Replaces scattered open-data portals and costly vendor negotiations with a single subscription."
                 },
                 {
                    icon: <Activity className="text-emerald-400" />,
                    title: "No ETL Required",
                    desc: "Simple schemas mean no data wrangling. We handle the projections, topology fixes, and cleaning."
                 },
                 {
                    icon: <Layers className="text-purple-400" />,
                    title: "Smart Filtering",
                    desc: "Query by category, administrative division, or bounding box. Get exactly the slice you need."
                 },
                 {
                    icon: <Server className="text-orange-400" />,
                    title: "Full Compatibility",
                    desc: "Works natively with Jupyter Notebooks, QGIS, ArcGIS, and modern BI systems like Tableau."
                 }
              ].map((item, i) => (
                 <div key={i} className="p-6 rounded-xl bg-slate-900/40 border border-slate-800 hover:bg-slate-900 transition-colors">
                    <div className="mb-4 w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
                       {item.icon}
                    </div>
                    <h4 className="text-lg font-bold text-slate-200 mb-2">{item.title}</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                 </div>
              ))}
           </div>
        </div>
      </section>

      {/* --- 4. Supported Tech Stack --- */}
      <section className="py-16 border-t border-slate-800 bg-slate-900/20">
        <div className="max-w-7xl mx-auto px-6 text-center">
           <p className="text-xs font-mono text-slate-500 uppercase tracking-[0.3em] mb-8">Seamless Integration With</p>
           <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center opacity-60 grayscale hover:grayscale-0 transition-all duration-700">
              {/* Logos represented as text for simplicity, would be SVGs in prod */}
              <span className="text-xl font-bold text-slate-300 flex items-center gap-2"><Globe size={20}/> REST API</span>
              <span className="text-xl font-bold text-pink-400 flex items-center gap-2"><Box size={20}/> GraphQL</span>
              <span className="text-xl font-bold text-blue-400 flex items-center gap-2"><Database size={20}/> GeoPackage</span>
              <span className="text-xl font-bold text-purple-400 flex items-center gap-2"><FileJson size={20}/> GeoJSON</span>
              <span className="text-xl font-bold text-yellow-400 flex items-center gap-2"><Terminal size={20}/> Python SDK</span>
              <span className="text-xl font-bold text-blue-300 flex items-center gap-2">R SDK</span>
           </div>
        </div>
      </section>

      {/* --- 5. Industry Use Cases --- */}
      <section className="py-24 bg-slate-950">
         <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold mb-12 text-center">Industries We Power</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               {[
                  { icon: <Building/>, name: "PropTech" },
                  { icon: <ShoppingCart/>, name: "Retail" },
                  { icon: <MapIcon/>, name: "Logistics" },
                  { icon: <Activity/>, name: "FinTech" },
                  { icon: <Zap/>, name: "Utilities" },
                  { icon: <Briefcase/>, name: "Public Sector" },
                  { icon: <Leaf/>, name: "AgriTech" },
                  { icon: <Shield/>, name: "Insurance" },
               ].map((ind, i) => (
                  <div key={i} className="flex flex-col items-center justify-center p-8 bg-slate-900 border border-slate-800 rounded-lg hover:border-emerald-500/30 hover:bg-slate-800 transition-all group cursor-default">
                     <div className="text-slate-500 group-hover:text-emerald-400 mb-3 transition-colors">
                        {React.cloneElement(ind.icon, { size: 32 })}
                     </div>
                     <span className="font-semibold text-slate-300 group-hover:text-white">{ind.name}</span>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* --- 6. Data We Offer --- */}
      <section className="py-24 bg-slate-900/50 border-y border-slate-800">
         <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
               <h2 className="text-3xl font-bold mb-4">Data We Offer</h2>
               <p className="text-slate-400">Curated, production-ready geospatial datasets for modern applications</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
               {/* POIs & Places */}
               <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-emerald-500/50 transition-all group">
                  <div className="w-14 h-14 bg-emerald-900/30 rounded-xl flex items-center justify-center mb-6 border border-emerald-500/20 text-emerald-400 group-hover:scale-110 transition-transform">
                     <MapPin size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">POIs & Places</h3>
                  <p className="text-slate-400 leading-relaxed">
                     Deep contextual datasets linking spatial points to commercial identity. Understand where businesses, services, and landmarks exist with rich attribution and categorization.
                  </p>
               </div>

               {/* Land Use & Land Cover */}
               <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-blue-500/50 transition-all group">
                  <div className="w-14 h-14 bg-blue-900/30 rounded-xl flex items-center justify-center mb-6 border border-blue-500/20 text-blue-400 group-hover:scale-110 transition-transform">
                     <Grid3x3 size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Land Use & Land Cover</h3>
                  <p className="text-slate-400 leading-relaxed">
                     Polygon coverage separating physical surface (water, forest) from functional zoning (industrial, residential). Essential for environmental analysis and urban planning.
                  </p>
               </div>

               {/* Buildings */}
               <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700 hover:border-purple-500/50 transition-all group">
                  <div className="w-14 h-14 bg-purple-900/30 rounded-xl flex items-center justify-center mb-6 border border-purple-500/20 text-purple-400 group-hover:scale-110 transition-transform">
                     <Home size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Buildings</h3>
                  <p className="text-slate-400 leading-relaxed">
                     Polygon footprints representing the roof and ground-floor geometry of structures, ready for 3D extrusion and shadow analysis. Perfect for urban modeling and real estate applications.
                  </p>
               </div>
            </div>

            {/* Coming Soon Banner */}
            <div className="text-center">
               <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-900/30 to-blue-900/30 border border-emerald-500/30 rounded-full">
                  <Zap size={18} className="text-emerald-400" />
                  <span className="text-slate-300 font-medium">Much more is coming soon</span>
                  <ArrowRight size={18} className="text-emerald-400" />
               </div>
            </div>
         </div>
      </section>

      {/* --- 7. Lead Capture Form (Strict Fields) --- */}
      <section id="waitlist" className="py-24 bg-gradient-to-b from-slate-900 to-slate-950 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <div className="bg-slate-950/80 backdrop-blur-md border border-slate-800 rounded-2xl p-8 sm:p-10 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Join the Waitlist</h2>
              <p className="text-slate-400">Get notified when our <span className="text-emerald-400">Spatial Data</span> catalog goes live.</p>
            </div>

            {/* Success Message */}
            {submitSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-medium">Thank you for joining!</p>
                  <p className="text-green-700 text-sm mt-1">
                    We've sent a confirmation email. Check your inbox!
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-1 uppercase">Full Name</label>
                <input 
                  type="text" 
                  name="name"
                  required
                  className="w-full bg-slate-900 border border-slate-700 rounded px-4 py-3 text-slate-100 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-1 uppercase">Contact Email</label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full bg-slate-900 border border-slate-700 rounded px-4 py-3 text-slate-100 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              {/* Industry Dropdown */}
              <div>
                <label className="block text-xs font-mono text-slate-500 mb-1 uppercase">Industry</label>
                <select 
                  name="industry"
                  className="w-full bg-slate-900 border border-slate-700 rounded px-4 py-3 text-slate-100 focus:border-emerald-500 outline-none cursor-pointer appearance-none"
                  value={formData.industry}
                  onChange={handleInputChange}
                >
                  <option>Real Estate</option>
                  <option>Retail</option>
                  <option>Logistics</option>
                  <option>Finance</option>
                  <option>Utilities</option>
                  <option>Government</option>
                  <option>Environmental</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Role Dropdown */}
              <div>
                 <label className="block text-xs font-mono text-slate-500 mb-1 uppercase">Role</label>
                 <select 
                  name="role"
                  className="w-full bg-slate-900 border border-slate-700 rounded px-4 py-3 text-slate-100 focus:border-emerald-500 outline-none cursor-pointer appearance-none"
                  value={formData.role}
                  onChange={handleInputChange}
                 >
                  <option>Data Analyst</option>
                  <option>Data Scientist</option>
                  <option>Developer</option>
                  <option>Product Manager</option>
                  <option>Executive</option>
                  <option>Other</option>
                 </select>
              </div>

              {/* Region Dropdown */}
              <div>
                 <label className="block text-xs font-mono text-slate-500 mb-1 uppercase">Region</label>
                 <select 
                  name="region"
                  className="w-full bg-slate-900 border border-slate-700 rounded px-4 py-3 text-slate-100 focus:border-emerald-500 outline-none cursor-pointer appearance-none"
                  value={formData.region}
                  onChange={handleInputChange}
                 >
                  <option>North America</option>
                  <option>South America</option>
                  <option>Europe</option>
                  <option>Africa</option>
                  <option>Asia</option>
                  <option>Oceania</option>
                  <option>Other</option>
                 </select>
              </div>

              <button 
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {submitting ? 'Submitting...' : 'Join the Waitlist'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* --- 7. Footer --- */}
      <footer className="bg-slate-950 py-10 border-t border-slate-900 text-center text-slate-600">
        <div className="flex items-center justify-center gap-2 mb-6">
           <Layers size={24} className="text-emerald-600"/>
           <span className="font-bold text-xl text-slate-300">GeoRTE</span>
        </div>
        <div className="flex justify-center gap-6 mb-8">
           <a href="#" className="hover:text-emerald-400 transition-colors"><Globe size={20}/></a>
           <a href="#" className="hover:text-emerald-400 transition-colors"><Terminal size={20}/></a>
           <a href="#" className="hover:text-emerald-400 transition-colors"><Anchor size={20}/></a>
        </div>
        <p className="text-sm">&copy; {new Date().getFullYear()} GeoRTE. All rights reserved.</p>
      </footer>
    </div>
  );
}